'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { flattenPortfolioAttribution, resolvePortfolioAttribution } from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import { useUtmParams } from '@/hooks/use-utm-params';
import { Analytics } from '@/lib/analytics';
import { type AccountCreationMethod, AUTH_EVENTS, type AuthEventBaseProperties, type AuthFlow, type AuthFunnelStep, type AuthStrategy, type AuthSurface, type SignUpChoiceClickedProperties } from '@/lib/analytics/auth-events';
import { getAnonymousCorrelationId } from '@/lib/analytics/get-anonymous-correlation-id';
import { pushGa4UserId } from '@/lib/analytics/push-ga4-user-id';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import type { FeatureFlagVariant } from '@/lib/feature-flags/types';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { usePasswordlessSignUpVariant } from './use-passwordless-sign-up-variant';
import { useSignUpOneTapVariant } from './use-sign-up-one-tap-variant';
type AuthEventProperties = AuthEventBaseProperties & Record<string, unknown>;
type AuthReturnScreen = 'start' | 'start_email_form' | 'email_verification' | 'verifications' | 'choose_strategy' | 'forgot_password' | 'reset_password';
interface IdentifyAuthUserParams {
  accountCreationMethod?: AccountCreationMethod;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  newsletter?: boolean;
  userId?: string;
}
function buildBaseProperties(passwordlessVariant: FeatureFlagVariant | undefined, oneTapVariant: FeatureFlagVariant | undefined, utm: {
  campaign: string;
  content: string;
  medium: string;
  source: string;
  referrer: string;
  partner: string;
}, allowlistedParams: string, anonymousId: string, attributionProps: ReturnType<typeof flattenPortfolioAttribution>, overrides?: Partial<AuthEventProperties>): AuthEventProperties {
  const params = allowlistedParams ? Object.fromEntries(new URLSearchParams(allowlistedParams).entries()) : undefined;
  return {
    ...(passwordlessVariant !== undefined && {
      ab_test_passwordless_sign_up_2026_q3: passwordlessVariant
    }),
    // On every auth event, both arms, so the read can split `auth_screen_viewed` by arm the way the
    // passwordless read does.
    ...(oneTapVariant !== undefined && {
      ab_test_google_one_tap_2026_q4: oneTapVariant
    }),
    ...{
      anonymous_id: anonymousId ?? ''
    },
    ...(utm.campaign && {
      utm_campaign: utm.campaign
    }),
    ...(utm.content && {
      utm_content: utm.content
    }),
    ...(utm.medium && {
      utm_medium: utm.medium
    }),
    ...(utm.source && {
      utm_source: utm.source
    }),
    ...(utm.referrer && {
      utm_referrer: utm.referrer
    }),
    ...(utm.partner && {
      partner: utm.partner
    }),
    ...attributionProps,
    ...(params && Object.keys(params).length > 0 && {
      search_params: params
    }),
    ...overrides
  };
}
export function useAuthEvents() {
  const $ = _c(180);
  const searchParams = useSearchParams();
  const {
    track
  } = useAnalytics();
  const {
    variant: passwordlessVariant
  } = usePasswordlessSignUpVariant();
  const {
    variant: oneTapVariant
  } = useSignUpOneTapVariant();
  const {
    campaign,
    content,
    medium,
    source,
    referrer,
    partner,
    portfolioCode,
    portfolioUrl,
    searchParamFallbacks
  } = useUtmParams();
  let t0;
  if ($[0] !== searchParamFallbacks || $[1] !== searchParams) {
    t0 = getAllowlistedSearchParams(searchParams, searchParamFallbacks);
    $[0] = searchParamFallbacks;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const allowlistedParams = t0;
  let t1;
  if ($[3] !== content || $[4] !== portfolioCode || $[5] !== portfolioUrl) {
    t1 = resolvePortfolioAttribution({
      portfolioCode,
      portfolioUrl,
      utmContent: content,
      referrer: null
    });
    $[3] = content;
    $[4] = portfolioCode;
    $[5] = portfolioUrl;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const portfolioAttribution = t1;
  let t2;
  if ($[7] !== portfolioAttribution) {
    t2 = flattenPortfolioAttribution(portfolioAttribution);
    $[7] = portfolioAttribution;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const attributionProps = t2;
  let t3;
  if ($[9] !== allowlistedParams || $[10] !== attributionProps || $[11] !== campaign || $[12] !== content || $[13] !== medium || $[14] !== oneTapVariant || $[15] !== partner || $[16] !== passwordlessVariant || $[17] !== referrer || $[18] !== source) {
    t3 = overrides => buildBaseProperties(passwordlessVariant, oneTapVariant, {
      campaign,
      content,
      medium,
      source,
      referrer,
      partner
    }, allowlistedParams, getAnonymousCorrelationId() ?? "", attributionProps, overrides);
    $[9] = allowlistedParams;
    $[10] = attributionProps;
    $[11] = campaign;
    $[12] = content;
    $[13] = medium;
    $[14] = oneTapVariant;
    $[15] = partner;
    $[16] = passwordlessVariant;
    $[17] = referrer;
    $[18] = source;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const baseProps = t3;
  let t4;
  if ($[20] !== track) {
    t4 = (event, properties, callback) => {
      track(event, properties, callback);
    };
    $[20] = track;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  const trackAuthEvent = t4;
  let t5;
  if ($[22] !== baseProps || $[23] !== trackAuthEvent) {
    t5 = (flow, currentScreen, step, extra) => {
      trackAuthEvent(AUTH_EVENTS.AUTH_SCREEN_VIEWED, baseProps({
        flow,
        step,
        current_screen: currentScreen,
        ...extra
      }));
    };
    $[22] = baseProps;
    $[23] = trackAuthEvent;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const trackAuthScreenViewed = t5;
  let t6;
  if ($[25] !== baseProps) {
    t6 = t7 => {
      const {
        userId,
        email,
        firstName,
        lastName,
        newsletter,
        accountCreationMethod
      } = t7;
      if (userId) {
        pushGa4UserId(userId);
      }
      Analytics.identify(userId, baseProps({
        ...(email && {
          email
        }),
        first_name: firstName ?? "",
        last_name: lastName ?? "",
        ...(newsletter !== undefined && {
          newsletter
        }),
        ...(accountCreationMethod && {
          account_creation_method: accountCreationMethod
        })
      }));
    };
    $[25] = baseProps;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const identifyAuthUser = t6;
  let t7;
  if ($[27] !== baseProps || $[28] !== trackAuthEvent) {
    t7 = (flow_0, step_0, errorCode, properties_0) => {
      trackAuthEvent(AUTH_EVENTS.AUTH_ERROR, baseProps({
        flow: flow_0,
        step: step_0,
        result: "error",
        ...(errorCode && {
          error_code: errorCode
        }),
        ...properties_0
      }));
    };
    $[27] = baseProps;
    $[28] = trackAuthEvent;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const trackAuthError = t7;
  let t8;
  if ($[30] !== baseProps || $[31] !== trackAuthEvent) {
    t8 = strategy => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_STRATEGY_SELECTED, baseProps({
        flow: "sign_in",
        step: "choose_strategy",
        strategy,
        method: "email"
      }));
    };
    $[30] = baseProps;
    $[31] = trackAuthEvent;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const trackSignInStrategySelected = t8;
  let t9;
  if ($[33] !== baseProps || $[34] !== trackAuthEvent) {
    t9 = (choice, properties_1) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_SESSION_SELECTED, baseProps({
        flow: "sign_in",
        step: "choose_session",
        choice,
        ...properties_1
      }));
    };
    $[33] = baseProps;
    $[34] = trackAuthEvent;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const trackSignInSessionSelected = t9;
  let t10;
  if ($[36] !== baseProps || $[37] !== trackAuthEvent) {
    t10 = (choice_0, properties_2) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_SESSION_SELECTED, baseProps({
        flow: "sign_up",
        step: "choose_session",
        choice: choice_0,
        ...properties_2
      }));
    };
    $[36] = baseProps;
    $[37] = trackAuthEvent;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const trackSignUpSessionSelected = t10;
  let t11;
  if ($[39] !== baseProps || $[40] !== trackAuthEvent) {
    t11 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_FORGOT_PASSWORD_OPENED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email"
      }));
    };
    $[39] = baseProps;
    $[40] = trackAuthEvent;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const trackSignInForgotPasswordOpened = t11;
  let t12;
  if ($[42] !== baseProps || $[43] !== trackAuthEvent) {
    t12 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_RESET_CODE_REQUESTED, baseProps({
        flow: "sign_in",
        step: "forgot_password",
        method: "email"
      }));
    };
    $[42] = baseProps;
    $[43] = trackAuthEvent;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const trackSignInResetCodeRequested = t12;
  let t13;
  if ($[45] !== baseProps || $[46] !== trackAuthEvent) {
    t13 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_RESET_CODE_SUBMITTED, baseProps({
        flow: "sign_in",
        step: "reset_password",
        method: "email"
      }));
    };
    $[45] = baseProps;
    $[46] = trackAuthEvent;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const trackSignInResetCodeSubmitted = t13;
  let t14;
  if ($[48] !== baseProps || $[49] !== trackAuthEvent) {
    t14 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_RESET_CODE_RESENT, baseProps({
        flow: "sign_in",
        step: "reset_password",
        method: "email"
      }));
    };
    $[48] = baseProps;
    $[49] = trackAuthEvent;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const trackSignInResetCodeResent = t14;
  let t15;
  if ($[51] !== baseProps || $[52] !== trackAuthEvent) {
    t15 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_NEW_PASSWORD_SUBMITTED, baseProps({
        flow: "sign_in",
        step: "reset_password",
        method: "email"
      }));
    };
    $[51] = baseProps;
    $[52] = trackAuthEvent;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  const trackSignInNewPasswordSubmitted = t15;
  let t16;
  if ($[54] !== baseProps || $[55] !== trackAuthEvent) {
    t16 = strategy_0 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_CODE_RESENT, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email",
        ...(strategy_0 && {
          strategy: strategy_0
        })
      }));
    };
    $[54] = baseProps;
    $[55] = trackAuthEvent;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  const trackSignInCodeResent = t16;
  let t17;
  if ($[57] !== baseProps || $[58] !== trackAuthEvent) {
    t17 = (choice_1, newsletter_0, t18) => {
      const surface = t18 === undefined ? "button" : t18;
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_CHOICE_CLICKED, {
        ...baseProps({
          newsletter: newsletter_0,
          flow: "sign_up",
          step: "start",
          surface
        }),
        choice: choice_1
      });
    };
    $[57] = baseProps;
    $[58] = trackAuthEvent;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  const trackSignUpChoiceClicked = t17;
  let t18;
  if ($[60] !== baseProps || $[61] !== trackAuthEvent) {
    t18 = (currentScreen_0, redirectedScreen) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_RETURN_CLICKED, baseProps({
        flow: "sign_up",
        method: "email",
        current_screen: currentScreen_0,
        redirected_screen: redirectedScreen
      }));
    };
    $[60] = baseProps;
    $[61] = trackAuthEvent;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  const trackSignUpEmailReturnClicked = t18;
  let t19;
  if ($[63] !== baseProps || $[64] !== trackAuthEvent) {
    t19 = (accountCreationMethod_0, newsletter_1, passwordChosen, t20) => {
      const surface_0 = t20 === undefined ? "button" : t20;
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_STARTED, baseProps({
        flow: "sign_up",
        step: "start",
        newsletter: newsletter_1,
        account_creation_method: accountCreationMethod_0,
        surface: surface_0,
        ...(passwordChosen === undefined ? {} : {
          password_chosen: passwordChosen
        })
      }));
    };
    $[63] = baseProps;
    $[64] = trackAuthEvent;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  const trackSignUpStarted = t19;
  let t20;
  if ($[66] !== baseProps || $[67] !== trackAuthEvent) {
    t20 = choice_2 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_PASSWORD_OPTION_TOGGLED, baseProps({
        flow: "sign_up",
        step: "start",
        method: "email",
        choice: choice_2
      }));
    };
    $[66] = baseProps;
    $[67] = trackAuthEvent;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  const trackSignUpPasswordOptionToggled = t20;
  let t21;
  if ($[69] !== baseProps || $[70] !== trackAuthEvent) {
    t21 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_CODE_SENT, baseProps({
        flow: "sign_up",
        step: "email_verification",
        method: "email",
        account_creation_method: "email"
      }));
    };
    $[69] = baseProps;
    $[70] = trackAuthEvent;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  const trackSignUpEmailCodeSent = t21;
  let t22;
  if ($[72] !== baseProps || $[73] !== trackAuthEvent) {
    t22 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_VERIFIED, baseProps({
        flow: "sign_up",
        step: "email_verification",
        method: "email",
        result: "success",
        account_creation_method: "email"
      }));
    };
    $[72] = baseProps;
    $[73] = trackAuthEvent;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  const trackSignUpEmailVerified = t22;
  let t23;
  if ($[75] !== baseProps || $[76] !== trackAuthEvent) {
    t23 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EXISTING_ACCOUNT_HANDOFF, baseProps({
        flow: "sign_up",
        step: "start",
        method: "email"
      }));
    };
    $[75] = baseProps;
    $[76] = trackAuthEvent;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  const trackSignUpExistingAccountHandoff = t23;
  let t24;
  if ($[78] !== baseProps || $[79] !== trackAuthEvent) {
    t24 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_CODE_RESENT, baseProps({
        flow: "sign_up",
        step: "email_verification",
        method: "email",
        account_creation_method: "email"
      }));
    };
    $[78] = baseProps;
    $[79] = trackAuthEvent;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  const trackSignUpEmailCodeResent = t24;
  let t25;
  if ($[81] !== baseProps || $[82] !== trackAuthEvent) {
    t25 = (accountCreationMethod_1, hasSession) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_WEBSITE_CALLBACK_ARRIVED, baseProps({
        flow: "sign_up",
        step: "website_callback",
        ...(accountCreationMethod_1 && {
          account_creation_method: accountCreationMethod_1
        }),
        has_session: hasSession
      }));
    };
    $[81] = baseProps;
    $[82] = trackAuthEvent;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  const trackSignUpWebsiteCallbackArrived = t25;
  let t26;
  if ($[84] !== baseProps || $[85] !== trackAuthEvent) {
    t26 = t27 => {
      const surface_1 = t27 === undefined ? "button" : t27;
      trackAuthEvent(AUTH_EVENTS.SSO_SIGN_UP_COMPLETED, baseProps({
        flow: "sign_up",
        step: "sso_callback",
        method: "google",
        account_creation_method: "google",
        surface: surface_1
      }));
    };
    $[84] = baseProps;
    $[85] = trackAuthEvent;
    $[86] = t26;
  } else {
    t26 = $[86];
  }
  const trackSsoSignUpCompleted = t26;
  let t27;
  if ($[87] !== baseProps || $[88] !== trackAuthEvent) {
    t27 = t28 => {
      const surface_2 = t28 === undefined ? "button" : t28;
      trackAuthEvent(AUTH_EVENTS.SSO_SIGN_IN_COMPLETED, baseProps({
        flow: "sign_in",
        step: "sso_callback",
        method: "google",
        account_creation_method: "google",
        surface: surface_2
      }));
    };
    $[87] = baseProps;
    $[88] = trackAuthEvent;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  const trackSsoSignInCompleted = t27;
  let t28;
  if ($[90] !== baseProps || $[91] !== trackAuthEvent) {
    t28 = () => {
      trackAuthEvent(AUTH_EVENTS.GOOGLE_ONE_TAP_PROMPTED, baseProps({
        flow: "sign_up",
        step: "start",
        method: "google",
        surface: "one_tap"
      }));
    };
    $[90] = baseProps;
    $[91] = trackAuthEvent;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  const trackGoogleOneTapPrompted = t28;
  let t29;
  if ($[93] !== baseProps || $[94] !== trackAuthEvent) {
    t29 = (momentType, reason) => {
      trackAuthEvent(AUTH_EVENTS.GOOGLE_ONE_TAP_PROMPT_MOMENT, baseProps({
        flow: "sign_up",
        step: "start",
        method: "google",
        surface: "one_tap",
        one_tap_moment: momentType,
        one_tap_reason: reason
      }));
    };
    $[93] = baseProps;
    $[94] = trackAuthEvent;
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  const trackGoogleOneTapPromptMoment = t29;
  let t30;
  if ($[96] !== baseProps || $[97] !== trackAuthEvent) {
    t30 = () => {
      trackAuthEvent(AUTH_EVENTS.GOOGLE_ONE_TAP_CREDENTIAL_RECEIVED, baseProps({
        flow: "sign_up",
        step: "start",
        method: "google",
        surface: "one_tap"
      }));
    };
    $[96] = baseProps;
    $[97] = trackAuthEvent;
    $[98] = t30;
  } else {
    t30 = $[98];
  }
  const trackGoogleOneTapCredentialReceived = t30;
  let t31;
  if ($[99] !== baseProps || $[100] !== trackAuthEvent) {
    t31 = errorCode_0 => {
      trackAuthEvent(AUTH_EVENTS.GOOGLE_ONE_TAP_FAILED, baseProps({
        flow: "sign_up",
        step: "start",
        method: "google",
        surface: "one_tap",
        error_code: errorCode_0
      }));
    };
    $[99] = baseProps;
    $[100] = trackAuthEvent;
    $[101] = t31;
  } else {
    t31 = $[101];
  }
  const trackGoogleOneTapFailed = t31;
  let t32;
  if ($[102] !== baseProps || $[103] !== trackAuthEvent) {
    t32 = direction => {
      trackAuthEvent(AUTH_EVENTS.SSO_TRANSFER_USED, baseProps({
        flow: direction === "sign_up_to_sign_in" ? "sign_up" : "sign_in",
        step: "sso_callback",
        sso_transfer_direction: direction
      }));
    };
    $[102] = baseProps;
    $[103] = trackAuthEvent;
    $[104] = t32;
  } else {
    t32 = $[104];
  }
  const trackSsoTransferUsed = t32;
  let t33;
  if ($[105] !== baseProps || $[106] !== trackAuthEvent) {
    t33 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_EXISTING_SESSION_REUSED, baseProps({
        step: "sso_callback"
      }));
    };
    $[105] = baseProps;
    $[106] = trackAuthEvent;
    $[107] = t33;
  } else {
    t33 = $[107];
  }
  const trackSsoExistingSessionReused = t33;
  let t34;
  if ($[108] !== baseProps || $[109] !== trackAuthEvent) {
    t34 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_FALLBACK_TO_SIGN_IN, baseProps({
        flow: "sign_in",
        step: "sso_callback"
      }));
    };
    $[108] = baseProps;
    $[109] = trackAuthEvent;
    $[110] = t34;
  } else {
    t34 = $[110];
  }
  const trackSsoFallbackToSignIn = t34;
  let t35;
  if ($[111] !== baseProps || $[112] !== trackAuthEvent) {
    t35 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_FALLBACK_TO_SIGN_UP, baseProps({
        flow: "sign_up",
        step: "sso_callback"
      }));
    };
    $[111] = baseProps;
    $[112] = trackAuthEvent;
    $[113] = t35;
  } else {
    t35 = $[113];
  }
  const trackSsoFallbackToSignUp = t35;
  let t36;
  if ($[114] !== baseProps || $[115] !== trackAuthEvent) {
    t36 = (recovery, properties_3) => {
      trackAuthEvent(AUTH_EVENTS.SSO_SIGN_UP_RECOVERY_STARTED, baseProps({
        flow: "sign_up",
        step: "sso_callback",
        method: "google",
        sso_recovery: recovery,
        ...properties_3
      }));
    };
    $[114] = baseProps;
    $[115] = trackAuthEvent;
    $[116] = t36;
  } else {
    t36 = $[116];
  }
  const trackSsoSignUpRecoveryStarted = t36;
  let t37;
  if ($[117] !== baseProps || $[118] !== trackAuthEvent) {
    t37 = accountCreationMethod_2 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_STARTED, baseProps({
        flow: "sign_in",
        step: "start",
        account_creation_method: accountCreationMethod_2
      }));
    };
    $[117] = baseProps;
    $[118] = trackAuthEvent;
    $[119] = t37;
  } else {
    t37 = $[119];
  }
  const trackSignInStarted = t37;
  let t38;
  if ($[120] !== baseProps || $[121] !== trackAuthEvent) {
    t38 = (currentScreen_1, redirectedScreen_0) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_EMAIL_RETURN_CLICKED, baseProps({
        flow: "sign_in",
        method: "email",
        current_screen: currentScreen_1,
        redirected_screen: redirectedScreen_0
      }));
    };
    $[120] = baseProps;
    $[121] = trackAuthEvent;
    $[122] = t38;
  } else {
    t38 = $[122];
  }
  const trackSignInEmailReturnClicked = t38;
  let t39;
  if ($[123] !== baseProps || $[124] !== trackAuthEvent) {
    t39 = strategy_1 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_MFA_STARTED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email",
        strategy: strategy_1
      }));
    };
    $[123] = baseProps;
    $[124] = trackAuthEvent;
    $[125] = t39;
  } else {
    t39 = $[125];
  }
  const trackSignInMfaStarted = t39;
  let t40;
  if ($[126] !== baseProps || $[127] !== trackAuthEvent) {
    t40 = t41 => {
      const strategy_2 = t41 === undefined ? "email_code" : t41;
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_MFA_COMPLETED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email",
        strategy: strategy_2
      }));
    };
    $[126] = baseProps;
    $[127] = trackAuthEvent;
    $[128] = t40;
  } else {
    t40 = $[128];
  }
  const trackSignInMfaCompleted = t40;
  let t41;
  if ($[129] !== baseProps || $[130] !== trackAuthEvent) {
    t41 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_MFA_BACKUP_CODE_SELECTED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email"
      }));
    };
    $[129] = baseProps;
    $[130] = trackAuthEvent;
    $[131] = t41;
  } else {
    t41 = $[131];
  }
  const trackSignInMfaBackupCodeSelected = t41;
  let t42;
  if ($[132] !== baseProps || $[133] !== trackAuthEvent) {
    t42 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_COMPLETED, baseProps({
        flow: "sign_in",
        result: "success"
      }));
    };
    $[132] = baseProps;
    $[133] = trackAuthEvent;
    $[134] = t42;
  } else {
    t42 = $[134];
  }
  const trackSignInCompleted = t42;
  let t43;
  if ($[135] !== baseProps || $[136] !== trackAuthEvent) {
    t43 = redirectUrl => {
      trackAuthEvent(AUTH_EVENTS.AUTH_UNAUTHENTICATED_REDIRECTED, baseProps({
        flow: "sign_in",
        step: "start",
        ...(redirectUrl && {
          redirect_url: redirectUrl
        })
      }));
    };
    $[135] = baseProps;
    $[136] = trackAuthEvent;
    $[137] = t43;
  } else {
    t43 = $[137];
  }
  const trackAuthUnauthenticatedRedirected = t43;
  let t44;
  if ($[138] !== baseProps || $[139] !== identifyAuthUser || $[140] !== trackAuthError || $[141] !== trackAuthEvent || $[142] !== trackAuthScreenViewed || $[143] !== trackAuthUnauthenticatedRedirected || $[144] !== trackGoogleOneTapCredentialReceived || $[145] !== trackGoogleOneTapFailed || $[146] !== trackGoogleOneTapPromptMoment || $[147] !== trackGoogleOneTapPrompted || $[148] !== trackSignInCodeResent || $[149] !== trackSignInCompleted || $[150] !== trackSignInEmailReturnClicked || $[151] !== trackSignInForgotPasswordOpened || $[152] !== trackSignInMfaBackupCodeSelected || $[153] !== trackSignInMfaCompleted || $[154] !== trackSignInMfaStarted || $[155] !== trackSignInNewPasswordSubmitted || $[156] !== trackSignInResetCodeRequested || $[157] !== trackSignInResetCodeResent || $[158] !== trackSignInResetCodeSubmitted || $[159] !== trackSignInSessionSelected || $[160] !== trackSignInStarted || $[161] !== trackSignInStrategySelected || $[162] !== trackSignUpChoiceClicked || $[163] !== trackSignUpEmailCodeResent || $[164] !== trackSignUpEmailCodeSent || $[165] !== trackSignUpEmailReturnClicked || $[166] !== trackSignUpEmailVerified || $[167] !== trackSignUpExistingAccountHandoff || $[168] !== trackSignUpPasswordOptionToggled || $[169] !== trackSignUpSessionSelected || $[170] !== trackSignUpStarted || $[171] !== trackSignUpWebsiteCallbackArrived || $[172] !== trackSsoExistingSessionReused || $[173] !== trackSsoFallbackToSignIn || $[174] !== trackSsoFallbackToSignUp || $[175] !== trackSsoSignInCompleted || $[176] !== trackSsoSignUpCompleted || $[177] !== trackSsoSignUpRecoveryStarted || $[178] !== trackSsoTransferUsed) {
    t44 = {
      trackAuthEvent,
      trackAuthScreenViewed,
      trackSignInSessionSelected,
      trackSignUpSessionSelected,
      trackSignInForgotPasswordOpened,
      trackSignInResetCodeRequested,
      trackSignInResetCodeSubmitted,
      trackSignInResetCodeResent,
      trackSignInNewPasswordSubmitted,
      trackSignInCodeResent,
      trackSignUpChoiceClicked,
      trackSignUpEmailReturnClicked,
      trackSignUpStarted,
      trackSignUpPasswordOptionToggled,
      trackSignUpEmailCodeSent,
      trackSignUpEmailVerified,
      trackSignUpEmailCodeResent,
      trackSignUpExistingAccountHandoff,
      trackSignUpWebsiteCallbackArrived,
      trackSsoSignUpCompleted,
      trackSsoSignInCompleted,
      trackSsoTransferUsed,
      trackSsoExistingSessionReused,
      trackSsoFallbackToSignIn,
      trackSsoFallbackToSignUp,
      trackSsoSignUpRecoveryStarted,
      trackGoogleOneTapPrompted,
      trackGoogleOneTapPromptMoment,
      trackGoogleOneTapCredentialReceived,
      trackGoogleOneTapFailed,
      trackSignInStarted,
      trackSignInEmailReturnClicked,
      trackSignInMfaStarted,
      trackSignInMfaCompleted,
      trackSignInMfaBackupCodeSelected,
      trackSignInCompleted,
      trackSignInStrategySelected,
      trackAuthUnauthenticatedRedirected,
      trackAuthError,
      identifyAuthUser,
      baseProps
    };
    $[138] = baseProps;
    $[139] = identifyAuthUser;
    $[140] = trackAuthError;
    $[141] = trackAuthEvent;
    $[142] = trackAuthScreenViewed;
    $[143] = trackAuthUnauthenticatedRedirected;
    $[144] = trackGoogleOneTapCredentialReceived;
    $[145] = trackGoogleOneTapFailed;
    $[146] = trackGoogleOneTapPromptMoment;
    $[147] = trackGoogleOneTapPrompted;
    $[148] = trackSignInCodeResent;
    $[149] = trackSignInCompleted;
    $[150] = trackSignInEmailReturnClicked;
    $[151] = trackSignInForgotPasswordOpened;
    $[152] = trackSignInMfaBackupCodeSelected;
    $[153] = trackSignInMfaCompleted;
    $[154] = trackSignInMfaStarted;
    $[155] = trackSignInNewPasswordSubmitted;
    $[156] = trackSignInResetCodeRequested;
    $[157] = trackSignInResetCodeResent;
    $[158] = trackSignInResetCodeSubmitted;
    $[159] = trackSignInSessionSelected;
    $[160] = trackSignInStarted;
    $[161] = trackSignInStrategySelected;
    $[162] = trackSignUpChoiceClicked;
    $[163] = trackSignUpEmailCodeResent;
    $[164] = trackSignUpEmailCodeSent;
    $[165] = trackSignUpEmailReturnClicked;
    $[166] = trackSignUpEmailVerified;
    $[167] = trackSignUpExistingAccountHandoff;
    $[168] = trackSignUpPasswordOptionToggled;
    $[169] = trackSignUpSessionSelected;
    $[170] = trackSignUpStarted;
    $[171] = trackSignUpWebsiteCallbackArrived;
    $[172] = trackSsoExistingSessionReused;
    $[173] = trackSsoFallbackToSignIn;
    $[174] = trackSsoFallbackToSignUp;
    $[175] = trackSsoSignInCompleted;
    $[176] = trackSsoSignUpCompleted;
    $[177] = trackSsoSignUpRecoveryStarted;
    $[178] = trackSsoTransferUsed;
    $[179] = t44;
  } else {
    t44 = $[179];
  }
  return t44;
}